import { useState } from "react";
import { Mail } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { act } from "@/components/common";
import { toast } from "sonner";

const CATS = [
  ["approvals", "Approval requests", "Items waiting for your decision, with one-click Approve / Reject, plus reminders"],
  ["decisions", "Decisions on my items", "When something you raised is approved or rejected"],
  ["breaches", "Tolerance breaches", "Cost or schedule moves out of tolerance"],
  ["escalations", "Escalations", "Items escalated by timers or RAID escalation flags"],
  ["benefits", "Benefits reviews", "Quarterly requests to update the benefits you own"],
  ["cab_pack", "CAB decision pack", "Sent 2 days before each CAB (CAB members only)"],
  ["weekly_report", "Weekly portfolio report", "Mondays 08:00 London time, before the progress meeting (PMO and project managers)"],
  ["digest", "Daily digest at 07:30", "Weekdays, London time — everything awaiting you"],
];
const KEYS = CATS.map(([k]) => k);
const PRESETS = {
  all: ["All", KEYS],
  approvals: ["Approvals only", ["approvals"]],
  breaches: ["Breaches only", ["breaches"]],
  digest: ["Digest only", ["digest"]],
  none: ["None", []],
};

const initial = (p = {}) => Object.fromEntries(KEYS.map((k) => [k, p[k] ?? (k === "digest" ? p.digest ?? true : p.instant ?? true)]));

export function EmailPrefs({ user }) {
  const [prefs, setPrefs] = useState(initial(user.email_prefs));
  const save = (p) => {
    setPrefs(p);
    act(api.put("/me/email-prefs", p), "Email preferences saved");
  };
  const preset = (keys) => save(Object.fromEntries(KEYS.map((k) => [k, keys.includes(k)])));
  const sendDigest = () => act(api.post("/me/digest")).then((r) => r && toast.info(r.data.last ? `Digest ${r.data.last.status}${r.data.last.error ? `: ${r.data.last.error}` : ` to ${r.data.last.to}`}` : "Nothing to include in your digest right now"));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="email-prefs-btn" className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 transition-colors" title="Email notifications"><Mail className="h-5 w-5" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[80vh] w-96 space-y-3 overflow-y-auto" data-testid="email-prefs-panel">
        <div>
          <div className="text-sm font-semibold">Email notifications</div>
          <div className="text-xs text-slate-500">Sent to {user.email}. In-app notifications are always on.</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(PRESETS).map(([k, [label, keys]]) => (
            <button key={k} data-testid={`email-preset-${k}`} onClick={() => preset(keys)} className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors">{label}</button>
          ))}
        </div>
        {CATS.map(([k, l, h]) => (
          <label key={k} className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1 accent-indigo-600" data-testid={`email-pref-${k}`} checked={prefs[k]} onChange={() => save({ ...prefs, [k]: !prefs[k] })} />
            <span><b>{l}</b><span className="block text-xs text-slate-500">{h}</span></span>
          </label>
        ))}
        <Button size="sm" variant="outline" className="w-full" data-testid="send-my-digest-btn" onClick={sendDigest}>Email me my digest now</Button>
      </PopoverContent>
    </Popover>
  );
}
